import * as Sentry from '@sentry/react';

import type {
  AskSynclyAnswerType,
  QuestionAnswerBlockStatus,
} from '@/entities/ask-syncly/ask-syncly-constants';
import type {
  CurrentAnswerBlock,
  PlainTextAnswer,
  StatisticalAnalysisAnswer,
  TopicAnalysisAnswer,
} from '@/entities/ask-syncly/ask-syncly-types';
import { AuthService } from '@/entities/auth/auth-service';
import type { DateRange } from '@/entities/query/query-types';
import {
  createAuthenticatedSSEConnection,
  type SSEController,
  type SSEEvent,
  type SSEOptions,
} from '@/shared/api-client/sse-client';

import { buildPublicFilterWithDates } from '@/entities/insight';
import type { PublicFilter } from '../../../features/(insight)/shared/public-filter/use-public-filter';
import type { PublicFilterWithDates } from '../../post/api/index';

// ============================================================================
// Deep Research Specific Types & Functions
// ============================================================================

export type DeepResearchSSEEventType = 'message' | 'error' | 'done';

export type TransitResponse = {
  type: 'transit';
  content: QuestionAnswerBlockStatus;
};

export type ReasonResponse = {
  type: 'reason';
  content: string;
};

export type AnswerResponse = {
  type: 'answer';
  content: TopicContent | StatisticalContent | PlainTextContent;
};

export type TopicContent = {
  type: AskSynclyAnswerType.TOPIC_ANALYSIS;
  content: TopicAnalysisAnswer;
};

export type StatisticalContent = {
  type: AskSynclyAnswerType.STATISTICAL_ANALYSIS;
  content: StatisticalAnalysisAnswer;
};

export type PlainTextContent = {
  type: AskSynclyAnswerType.PLAIN_TEXT;
  content: PlainTextAnswer;
};

export type DeepResearchResponse = TransitResponse | ReasonResponse | AnswerResponse;

export interface DeepResearchSSEEvent extends SSEEvent<
  DeepResearchSSEEventType,
  DeepResearchResponse
> {
  type: DeepResearchSSEEventType;
  data: DeepResearchResponse;
}

export interface DeepResearchSSEOptions extends SSEOptions<
  DeepResearchSSEEventType,
  DeepResearchResponse
> {
  onMessage?: (event: DeepResearchSSEEvent) => void;
}

export type DeepResearchSSEController = SSEController;

/**
 * SSE를 통해 Deep Research 요청을 시작하고 실시간 업데이트를 받습니다.
 *
 * @param workspaceId - 워크스페이스 ID
 * @param queryId - 쿼리 ID
 * @param question - 질문 내용
 * @param options - SSE 이벤트 핸들러 옵션
 * @returns 연결을 제어할 수 있는 컨트롤러 객체
 */
export const requestDeepResearch = (params: {
  session_id: string;
  question: string;
  dateRange: DateRange | null;
  publicFilter: PublicFilter;
  options: DeepResearchSSEOptions;
}): DeepResearchSSEController => {
  const { session_id, question, dateRange, publicFilter, options } = params;
  const { onError } = options;

  // 토큰 가져오기
  const tokens = AuthService.getTokens();
  if (!tokens) {
    const error = new Error('No authentication tokens found');
    onError?.(error);
    Sentry.captureException(error);
    return {
      abort: () => {},
      isConnected: () => false,
    };
  }

  const baseUrl = import.meta.env.VITE_SOCIAL_API_BASE_URL;
  const url = `${baseUrl}/ask_syncly_sessions/${session_id}/ask`;

  const publicFilterBody: PublicFilterWithDates = buildPublicFilterWithDates(
    publicFilter,
    dateRange?.startDate,
    dateRange?.endDate,
  );

  // 추상화된 SSE 연결 함수 사용
  return createAuthenticatedSSEConnection<DeepResearchSSEEventType, Partial<CurrentAnswerBlock>>(
    {
      url,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        question: question,
        filter: {
          public: publicFilterBody,
        },
      },
    },
    {
      ...options,
      onMessage: (event) => {
        options.onMessage?.(event as DeepResearchSSEEvent);
      },
      onError: (error) => {
        options.onError?.(error);
        Sentry.captureException(error, {
          extra: {
            session_id,
            url,
          },
        });
      },
    },
  );
};
